export const VIDEO_MODES = ['text', 'frames', 'references', 'edit', 'extend'] as const
export const VIDEO_ASPECT_RATIOS = ['16:9', '9:16', '1:1'] as const
export const VIDEO_RESOLUTIONS = ['360p', '480p', '720p', '1080p', '4k'] as const

export type VideoMode = (typeof VIDEO_MODES)[number]
export type VideoAspectRatio = (typeof VIDEO_ASPECT_RATIOS)[number]
export type VideoResolution = (typeof VIDEO_RESOLUTIONS)[number]
export type VideoProvider = 'runway' | 'vertex'

export type VideoReferenceLimits = {
  images: number
  videos: number
  audio: number
  /** Combined duration of video/audio references and the source video. */
  combinedSeconds?: number
  /** Longest single video reference accepted. */
  maxVideoSeconds?: number
}

export type VideoModelCapabilityDefinition = {
  modes: readonly VideoMode[]
  durations: { min: number; max: number; step: number; values?: readonly number[] }
  aspectRatios: readonly VideoAspectRatio[]
  /** Use only when a provider supports different ratios for different modes. */
  aspectRatiosByMode?: Partial<Record<VideoMode, readonly VideoAspectRatio[]>>
  resolutions: readonly VideoResolution[]
  supportsLastFrame: boolean
  supportsAudio: boolean
  audioMode: 'unsupported' | 'optional' | 'included'
  supportsNegativePrompt: boolean
  promptMaxLength: number
  inputAspectRatio: { min: number; max: number }
  referenceLimits?: VideoReferenceLimits
  /** Overrides for modes whose endpoint accepts a different set of references. */
  referenceLimitsByMode?: Partial<Record<VideoMode, VideoReferenceLimits>>
  /** Combined size of every media input, including an uploaded or stored source video. */
  maxTotalMediaBytes?: number
  /** Edit/extend: longest source video accepted. */
  maxSourceVideoSeconds?: number
  /** Edit/extend: most videos per request, counting video references and the source. */
  maxInputVideos?: number
  /**
   * Continuing this model's own output reuses the provider's stored result by
   * ID instead of re-uploading it, so that source is exempt from
   * `maxSourceVideoSeconds` and `maxTotalMediaBytes`.
   */
  reusesOwnSourceOutput?: boolean
}

export type RunwayCreditPricing =
  | { kind: 'per-second'; creditsPerSecond: number }
  | { kind: 'per-second-with-audio'; creditsPerSecond: number; creditsPerSecondWithAudio: number }
  | {
      kind: 'resolution-based'
      minimumCredits: number
      creditsPerSecondByResolution: Partial<Record<VideoResolution, number>>
    }

export type GeminiVideoPricing = {
  estimatedCostPerSecondUsdByResolution: Partial<Record<VideoResolution, number>>
  estimatedImageInputCostUsd: number
}

export type VideoModelDefinition = {
  provider: VideoProvider
  providerModel: string
  modelName: string
  /** Short, user-facing description for model selection. */
  description?: string
  isPreview: boolean
  capabilities: VideoModelCapabilityDefinition
  providerRatios: Record<VideoResolution, Partial<Record<VideoAspectRatio, string>>>
  pricing: RunwayCreditPricing | GeminiVideoPricing
}

export type GeminiVideoModelDefinition = Omit<VideoModelDefinition, 'provider' | 'pricing'> & {
  provider: 'vertex'
  pricing: GeminiVideoPricing
}

export type RunwayVideoModelDefinition = Omit<VideoModelDefinition, 'provider' | 'pricing'> & {
  provider: 'runway'
  pricing: RunwayCreditPricing
  requestOptions: {
    includeAudio: boolean
    includeNegativePrompt: boolean
  }
}
